import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Seo from "@/components/Seo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    // Send portal="shopper" so the backend redirects admin accounts to
    // /admin/login instead of letting them bypass the portal separation.
    const res = await login(email, password, "shopper");
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      const role = res.user.role;
      const from = location.state?.from;
      if (from) navigate(from);
      else if (role === "admin" || role === "super_admin") navigate("/admin");
      else if (role === "merchant") navigate("/merchant");
      else navigate("/dashboard");
    } else if (res.wrongPortal) {
      toast.info("Admin accounts must sign in via the admin portal.");
      navigate(res.wrongPortal);
    } else setError(res.error);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 py-16 relative">
      <Seo
        title="Sign in"
        description="Sign in to your Cyannect account to manage campaigns, redemptions and cashback."
        path="/login"
        noindex
      />
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="blob blob-cyan blob-float w-[500px] h-[500px] top-10 -left-40" />
        <div className="blob blob-lav blob-float w-[500px] h-[500px] bottom-10 -right-40" style={{ animationDelay: "4s" }} />
      </div>

      <div className="max-w-md mx-auto relative z-10">
        <div className="glass-strong rounded-[28px] w-full p-8 fade-up">
          <img src="/cyannect-logo.png" alt="Cyannect" className="h-16 w-auto mx-auto mb-2" />
          <div className="pf-chip pf-chip-cyan mx-auto w-fit">Welcome back</div>
          <h1 className="font-display text-4xl font-medium tracking-tight mt-4 text-center text-slate-900">Log in</h1>
          <p className="text-sm text-slate-600 mt-2 text-center">Access your Cyannect account.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="pf-input w-full px-4 py-3 text-sm" data-testid="login-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pf-input w-full px-4 py-3 text-sm" data-testid="login-password" />
            </div>

            {error && <div className="text-sm text-red-600 bg-red-50/70 backdrop-blur border border-red-200 rounded-xl px-4 py-2" data-testid="login-error">{error}</div>}

            <button type="submit" disabled={loading} className="pf-btn-primary w-full py-3 rounded-full font-semibold" data-testid="login-submit">
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <div className="mt-3">
            <Link
              to="/phone-login"
              className="w-full block text-center py-3 rounded-full font-semibold border border-cyan-200 bg-white/60 hover:bg-white text-cyan-700 transition-colors text-sm"
              data-testid="login-phone-link"
            >
              📱 Sign in with phone (passwordless)
            </Link>
          </div>

          <div className="mt-4 text-center text-sm">
            <Link to="/forgot-password" className="text-slate-500 hover:text-cyan-700" data-testid="login-forgot-link">Forgot password?</Link>
          </div>

          <div className="mt-6 text-sm text-slate-600 text-center">
            No account? <Link to="/register" className="text-cyan-700 font-semibold hover:underline">Sign up</Link>
          </div>
          <p className="text-[11px] text-slate-500 mt-2 text-center">
            Merchant? <Link to="/register?role=merchant" className="text-cyan-700 font-semibold hover:underline" data-testid="merchant-signup-link">See our subscription plans &amp; sign up</Link>
          </p>
          {/* <p className="text-[11px] text-slate-400 mt-2 text-center">
            Admin? <Link to="/admin/login" className="text-slate-500 hover:text-slate-700 underline" data-testid="admin-portal-link">Sign in via the admin portal</Link>
          </p> */}
        </div>
      </div>
    </div>
  );
}
